import noImage from 'assets/icons/contact_noimage.svg';
import React from 'react';
import { useSelector } from 'react-redux';
import { selectActiveCompanyId } from '@mm/state/selectors/viewSelectors';
import { getPlanInfo } from '@mm/state/helpers/billing-info-helpers';
import { abbreviateNumber } from '@mm/utils';
import { EnhancedBillingPage } from '@mm/state/interfaces/connected-page';
import { Plan } from '@mm/state/interfaces/subscription';
import CompanyModel from '@mm/state/model/CompanyModel';
import { getMessagesStatistic } from '@mm/state/selectors/facebookPageSelector';
import { getShouldShowPercentageBar } from '@mm/state/selectors/subscriptionSelectors';
import classNames from 'classnames';
import { EditOutlined, PlusCircleOutlined } from '@ant-design/icons';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { ImageWithFallback } from '../../../../common/components/ImageWithFallback/ImageWithFallback';
import { PercentageBar } from '../../../../common/components/PercentageBar/PercentageBar';
import { PlanBadge } from '../../../../common/components/ProBadge';
import ExceedSendFooter from './ExceedSendFooter/ExceedSendFooter';
import './FacebookPageCard.pcss';
import {
  ChannelIcon,
  ChannelType,
  VISIBLE_CHANNELS,
} from '../../../../editor/features/liveChat/components/ChannelIcon';
import {
  CONNECTABLE_CHANNELS,
  useChannelConnection,
} from '../../../../editor/features/beta/modules/Automations/AutomationEdit/InputTriggers/use-channel-connection';

type Props = {
  company: CompanyModel;
  facebookPage: EnhancedBillingPage;
  onClick: () => Promise<void>;
  active?: boolean;
  plans: Plan[];
  onCustomize: () => void;
  followUpConvoCount?: number | string;
  userRoleInCompany?: string;
};

export const FacebookPageCard = (props: Props) => {
  const {
    facebookPage,
    onClick,
    active,
    plans,
    company,
    onCustomize,
    followUpConvoCount,
    userRoleInCompany,
  } = props;
  const cardClassName = `facebook-page-card__wrapper ${
    active ? 'facebook-page-card--wrapper-active' : ''
  }`;
  const messagesStatistic = getMessagesStatistic(facebookPage);
  const isOwnerOrAdmin = company && company.editable;
  const percentageBarTitle = `Credits used (out of ${abbreviateNumber(
    messagesStatistic.outgoingMessageLimit,
  )})`;
  const shouldShowPercentageBar = getShouldShowPercentageBar(
    facebookPage,
    plans,
  );
  const activeCompanyId = useSelector(selectActiveCompanyId);

  const exceedsSendsLimitVesibleByLockStrategy =
    facebookPage.lockStrategy === 'twilio_level'
      ? false
      : facebookPage.hardLimitReached;

  const plansInfo = getPlanInfo(facebookPage);

  const { isSMSSupported } = useChannelConnection(() => {});
  const modalActions = useModalActions();

  const isUserOwnerOrAdmin =
    userRoleInCompany === 'admin' || userRoleInCompany === 'owner';

  const showExceedsSendsFooter =
    !(plansInfo.isCustomTeam || plansInfo.isLegacyTeamPlan) &&
    exceedsSendsLimitVesibleByLockStrategy &&
    isOwnerOrAdmin &&
    !facebookPage.overagesEnabled;

  const { name } = facebookPage;

  const image =
    facebookPage.businessLogo?.urls?.small || facebookPage.squarePicture;

  const showConnectedChannels = (channelType: ChannelType) => {
    switch (channelType) {
      case 'instagram':
        return facebookPage.instagramActivated;
      case 'facebook':
        return facebookPage.realFacebookPage;
      case 'webchat':
        return facebookPage.webChatActivated;
      case 'sms':
        return (
          isSMSSupported &&
          facebookPage.smsActivated &&
          facebookPage?.smsNumberActivated
        );
      case 'email':
        return facebookPage.emailActivated;
      default:
        return;
    }
  };

  const channels = VISIBLE_CHANNELS.filter(showConnectedChannels);

  return (
    <div
      className={classNames('facebook-page-card', {
        'facebook-page-card__active': active,
      })}
    >
      <div className={cardClassName} onClick={onClick}>
        <ImageWithFallback
          src={image}
          fallbackImage={noImage}
          className="facebook-page-card__image"
          alt="Facebook Page"
        />

        <div className="facebook-page-card__section-meta">
          <div className="facebook-page-card__section-meta-top">
            <h3 className="facebook-page-card__name">{name}</h3>
            <div className="facebook-page-card__follow-up-and-badge-container">
              {followUpConvoCount && (
                <div className="facebook-page-card__follow-up-container">
                  <div className="facebook-page-card__follow-up-count">
                    <div>{followUpConvoCount}</div>
                  </div>
                  <div className="facebook-page-card__follow-up-tooltip">
                    Follow up conversations
                  </div>
                </div>
              )}
              <div className="facebook-page-card__pro-and-customize-icons">
                {!facebookPage.realFacebookPage && (
                  <div
                    onClick={e => {
                      e.stopPropagation();
                      onCustomize();
                    }}
                    className="facebook-page-card__customize-icon"
                  >
                    <EditOutlined className="facebook-page-card__edit-icon" />
                  </div>
                )}
                <PlanBadge
                  className="facebook-page-card__pro-icon"
                  facebookPage={facebookPage}
                  billingType={facebookPage.pageBillingType}
                  hideFreeIcon={true}
                />
              </div>
            </div>
          </div>
          <div className="facebook-page-card__middle-section">
            {channels?.map(channel => {
              return (
                <ChannelIcon
                  channel={channel}
                  key={channel}
                  className="facebook-page-card__channel"
                />
              );
            })}
            {isUserOwnerOrAdmin &&
              company.id === activeCompanyId &&
              channels.length < (isSMSSupported ? 5 : 4) && (
                <PlusCircleOutlined
                  className="facebook-page-card__add-channel"
                  color="#2F54EB"
                  onClick={e => {
                    e.stopPropagation();
                    modalActions.openConnectChannelModal({
                      channels: CONNECTABLE_CHANNELS.filter(channel =>
                        channel === 'sms' ? isSMSSupported : true,
                      ).filter(channel => !channels.includes(channel)),
                      facebookPage,
                    });
                  }}
                />
              )}
          </div>

          <div className="facebook-page-card__bottom-section">
            {shouldShowPercentageBar && !showExceedsSendsFooter && (
              <PercentageBar
                title={percentageBarTitle}
                cardLayout={true}
                total={messagesStatistic.outgoingMessageLimit}
                used={messagesStatistic.cappedCount}
                showTotalNumber={false}
                showUsedNumber={false}
                className="facebook-page-card__percentage-bar"
                showUsedNumberRightSide
              />
            )}
            {showExceedsSendsFooter && (
              <ExceedSendFooter
                messagesStatistic={messagesStatistic}
                facebookPage={facebookPage}
                showPercentageBar={shouldShowPercentageBar}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
